import * as React from "react";
import { Smartphone } from "lucide-react";

const PWAInstallContext = React.createContext<{ openInstallModal: () => void } | null>(null);

export function usePWAInstall() {
  return React.useContext(PWAInstallContext);
}

/** AppShell 등에서 감싸서 사용. 자식 트리에서 usePWAInstall() / PWAInstallLink 사용 가능 */
export function PWAInstallProvider({ children }: { children: React.ReactNode }) {
  const [installPrompt, setInstallPrompt] = React.useState<BeforeInstallPromptEvent | null>(null);
  const [isIOS, setIsIOS] = React.useState(false);
  const [isStandalone, setIsStandalone] = React.useState(false);
  const [modalOpen, setModalOpen] = React.useState(false);
  const modalRef = React.useRef<HTMLDialogElement>(null);
  const [installError, setInstallError] = React.useState<string | null>(null);
  const [installing, setInstalling] = React.useState(false);
  const openInstallModal = React.useCallback(() => setModalOpen(true), []);
  const contextValue = React.useMemo(() => ({ openInstallModal }), [openInstallModal]);

  React.useEffect(() => {
    const ua = typeof navigator !== "undefined" ? navigator.userAgent : "";
    setIsIOS(/iPad|iPhone|iPod/.test(ua) || (ua.includes("Mac") && "ontouchend" in document));
    setIsStandalone(
      typeof window !== "undefined" &&
        (window.matchMedia("(display-mode: standalone)").matches ||
          (window.navigator as unknown as { standalone?: boolean }).standalone === true)
    );
  }, []);

  React.useEffect(() => {
    const handler = (e: Event) => {
      e.preventDefault();
      setInstallPrompt(e as BeforeInstallPromptEvent);
    };
    window.addEventListener("beforeinstallprompt", handler);
    return () => window.removeEventListener("beforeinstallprompt", handler);
  }, []);

  React.useEffect(() => {
    if (!modalOpen || isStandalone) return;
    const dialog = modalRef.current;
    const previousOverflow = document.body.style.overflow;
    dialog?.showModal();
    document.body.style.overflow = "hidden";
    return () => { dialog?.close(); document.body.style.overflow = previousOverflow; };
  }, [modalOpen, isStandalone]);

  const handleInstall = async () => {
    if (!installPrompt) {
      setModalOpen(true);
      return;
    }
    if (installing) return;
    setInstalling(true); setInstallError(null);
    try {
      await installPrompt.prompt();
      const choice = await installPrompt.userChoice;
      setInstallPrompt(null);
      if (choice.outcome === "accepted") setModalOpen(false);
    } catch {
      setInstallError("설치를 시작하지 못했어요. 브라우저 메뉴에서 앱 설치를 다시 시도해 주세요.");
    } finally {
      setInstalling(false);
    }
  };

  return (
    <PWAInstallContext.Provider value={contextValue}>
      {children}
      {!isStandalone && modalOpen && (
        <dialog ref={modalRef} aria-labelledby="install-dialog-title" onCancel={() => setModalOpen(false)} onClick={event => { if (event.target === event.currentTarget) setModalOpen(false); }} className="fe-small-dialog">
          <div onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center gap-3 mb-4">
              <div className="fe-brand-mark shrink-0">
                <span>f.</span>
              </div>
              <div>
                <h2 id="install-dialog-title" className="text-lg font-bold text-gray-900">앱처럼 사용하기</h2>
                <p className="text-sm text-gray-500">Foreducator를 홈 화면에 추가하세요</p>
              </div>
            </div>
            {installPrompt ? (
              <div className="space-y-3">
                <p className="text-sm text-gray-600">
                  설치하면 앱처럼 전체 화면으로 쓸 수 있고, 홈 화면에서 바로 열 수 있어요.
                </p>
                <button
                  type="button"
                  onClick={() => void handleInstall()}
                  disabled={installing}
                  className="fe-button fe-button-dark w-full disabled:opacity-70"
                >
                  {installing ? "설치 중…" : "앱 설치하기"}
                </button>
              </div>
            ) : isIOS ? (
              <div className="space-y-3">
                <p className="text-sm text-gray-600">
                  <strong>iPhone / iPad</strong>에서는 Safari에서만 추가할 수 있어요.
                </p>
                <ol className="text-sm text-gray-700 list-decimal list-inside space-y-2">
                  <li>Safari에서 foreducator.com을 연다</li>
                  <li>하단 <strong>공유</strong> 버튼(□↑)을 누른다</li>
                  <li><strong>홈 화면에 추가</strong>를 누른다</li>
                  <li>추가를 눌러 완료한다</li>
                </ol>
                <p className="text-xs text-gray-500">Chrome 등 다른 브라우저에서는 Safari로 주소를 열어주세요.</p>
              </div>
            ) : (
              <p className="text-sm text-gray-600">
                이 브라우저에서는 주소창 메뉴(⋮)에서 &quot;앱 설치&quot; 또는 &quot;Foreducator 설치&quot;를 찾아 설치할 수 있어요.
              </p>
            )}
            {installError && <p role="alert" className="mt-3 text-sm text-red-700">{installError}</p>}
            <button type="button" onClick={() => setModalOpen(false)} className="mt-4 w-full py-2 text-sm text-gray-500 hover:text-gray-700">
              닫기
            </button>
          </div>
        </dialog>
      )}
    </PWAInstallContext.Provider>
  );
}

/** 푸터 등에 넣을 "앱 설치" 링크. PWAInstall이 상위에 있어야 동작함 */
export function PWAInstallLink() {
  const pwa = usePWAInstall();
  const [isStandalone, setIsStandalone] = React.useState(false);
  React.useEffect(() => {
    setIsStandalone(
      typeof window !== "undefined" &&
        (window.matchMedia("(display-mode: standalone)").matches ||
          (window.navigator as unknown as { standalone?: boolean }).standalone === true)
    );
  }, []);
  if (isStandalone || !pwa) return null;
  return (
    <button
      type="button"
      onClick={pwa.openInstallModal}
      className="text-sm text-gray-400 hover:text-white transition-colors flex items-center gap-1.5"
    >
      <Smartphone className="w-4 h-4" />
      앱 설치 / 홈 화면에 추가
    </button>
  );
}

declare global {
  interface WindowEventMap {
    beforeinstallprompt: BeforeInstallPromptEvent;
  }
}
interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: "accepted" | "dismissed" }>;
}
